import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { adminApi } from '../api';
import { Bell, Copy, Trash2, Check, User as UserIcon, Send, KeyRound, Inbox, ExternalLink, Eye, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const NotificationsDrawer = ({ open, onOpenChange, onChanged }) => {
  const [notifs, setNotifs] = useState([]);
  const [selections, setSelections] = useState([]);
  const [tab, setTab] = useState('notifications');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [n, s] = await Promise.all([adminApi.listNotifications(), adminApi.listSelections()]);
      setNotifs(n); setSelections(s);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const copyBody = (n) => { navigator.clipboard.writeText(n.body); toast.success('Testo copiato'); };

  const markRead = async (n) => {
    if (!n.seen) { await adminApi.markNotificationRead(n.id); await load(); onChanged && onChanged(); }
  };

  const removeNotif = async (n) => {
    if (!window.confirm('Eliminare questa notifica?')) return;
    await adminApi.deleteNotification(n.id); await load(); onChanged && onChanged();
  };

  const iconFor = (type) => {
    if (type === 'client_created') return <KeyRound className="w-4 h-4" />;
    if (type === 'selection') return <Send className="w-4 h-4" />;
    if (type === 'first_visit') return <Eye className="w-4 h-4" />;
    if (type === 'activity') return <Heart className="w-4 h-4" />;
    return <Bell className="w-4 h-4" />;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-[#141416] text-white border-l border-white/10 p-0">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="text-white font-display text-2xl flex items-center gap-2"><Bell className="w-5 h-5 text-[#C9A961]" /> Notifiche</SheetTitle>
          <SheetDescription className="text-slate-400">Le "email" che l'app avrebbe inviato e le selezioni dei clienti arrivano qui.</SheetDescription>
        </SheetHeader>

        <div className="px-6 pt-4 flex items-center gap-2 border-b border-white/10">
          <button onClick={()=>setTab('notifications')} className={`px-3 py-2 text-xs font-medium border-b-2 transition ${tab==='notifications' ? 'border-[#C9A961] text-white' : 'border-transparent text-slate-400 hover:text-white'}`}>
            <span className="inline-flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Notifiche ({notifs.length})</span>
          </button>
          <button onClick={()=>setTab('selections')} className={`px-3 py-2 text-xs font-medium border-b-2 transition ${tab==='selections' ? 'border-[#C9A961] text-white' : 'border-transparent text-slate-400 hover:text-white'}`}>
            <span className="inline-flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" /> Selezioni ({selections.length})</span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-3" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {loading && <p className="text-sm text-slate-500">Caricamento...</p>}

          {tab === 'notifications' && !loading && (
            notifs.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-10">Nessuna notifica ancora.</p>
            ) : notifs.map(n => (
              <div key={n.id} className={`rounded-xl border p-4 transition ${n.seen ? 'bg-white/5 border-white/10' : 'bg-[#C9A961]/5 border-[#C9A961]/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#C9A961]/20 text-[#C9A961] grid place-items-center">{iconFor(n.type)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{n.subject}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{n.type === 'client_created' ? 'Credenziali cliente' : n.type === 'selection' ? 'Selezione ricevuta' : n.type === 'first_visit' ? 'Prima visita' : n.type === 'activity' ? 'Like e commenti' : 'Info'} · {new Date(n.created_at).toLocaleString('it-IT', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</div>
                  </div>
                  {!n.seen && <span className="w-2 h-2 rounded-full bg-[#C9A961]" />}
                </div>
                <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans bg-black/30 rounded-lg p-3 border border-white/5">{n.body}</pre>
                {n.to && <p className="text-[10px] text-slate-500 mt-2">Destinatario: {n.to}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={()=>copyBody(n)} className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-full"><Copy className="w-3.5 h-3.5" /> Copia testo</button>
                  {n.meta?.client_slug && (
                    <a href={`/c/${n.meta.client_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-full"><ExternalLink className="w-3.5 h-3.5" /> Apri area</a>
                  )}
                  {!n.seen && <button onClick={()=>markRead(n)} className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-full"><Check className="w-3.5 h-3.5" /> Segna letta</button>}
                  <button onClick={()=>removeNotif(n)} className="ml-auto text-slate-500 hover:text-rose-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))
          )}

          {tab === 'selections' && !loading && (
            selections.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-10">Nessuna selezione ricevuta.</p>
            ) : selections.map(s => (
              <div key={s.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#C9A961]/20 text-[#C9A961] grid place-items-center"><UserIcon className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{s.client_name_display} · <span className="text-slate-400">{s.client_name}</span></div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.gallery_title} · {new Date(s.created_at).toLocaleString('it-IT', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</div>
                  </div>
                  <span className="text-xs bg-[#C9A961]/20 text-[#C9A961] px-2 py-0.5 rounded-full font-bold">{s.photos.length} foto</span>
                </div>
                {s.note && <p className="text-xs text-slate-300 italic bg-black/30 rounded-lg p-2 mb-2">“{s.note}”</p>}
                <div className="grid grid-cols-6 gap-1.5 mt-2">
                  {s.photos.slice(0, 18).map(p => (
                    <div key={p.id} className="aspect-square rounded overflow-hidden bg-black">
                      <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {s.photos.length > 18 && <div className="aspect-square rounded bg-white/10 grid place-items-center text-[10px] text-slate-400">+{s.photos.length-18}</div>}
                </div>
                <div className="mt-3">
                  <a href={`/c/${s.client_slug}/lavori/${s.gallery_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-[#C9A961] hover:bg-white text-black font-semibold px-3 py-1.5 rounded-full"><ExternalLink className="w-3.5 h-3.5" /> Apri galleria</a>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationsDrawer;
